import { Add } from '@mui/icons-material'
import {
  Button,
  Drawer,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  MasterFieldDef,
  MasterFieldTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { FC, useCallback, useId } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Header } from 'src/components/header/Header'
import { HeaderBackButtonMui } from 'src/components/header/HeaderBackButtonMui'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { gray } from 'src/features/muikds/kdsColors'
import { useModalClosePreventionIsPending } from 'src/hooks/useModalClosePrevention'
import { isHTTPError } from 'src/state/middleware/saga/handleHttpError'
import { useDrawerState } from '../../../hooks/useDrawerState'
import { useMutationAddMasterFieldDef } from '../../api/useMutationAddMasterFieldDef'

const MASTER_FIELD_DEF_LIMIT = 50

const ITEM_TYPE_OPTIONS = [
  { value: MasterFieldTypeEnum.Text, label: 'テキスト' },
  { value: MasterFieldTypeEnum.Number, label: '数値' },
] as const

type FormData = {
  fieldDefName: string
  fieldDefType: MasterFieldTypeEnum | ''
}

type Props = {
  masterUuid: string
  fieldDefs: MasterFieldDef[]
}

export const MasterFieldDefAddButton: FC<Props> = ({
  masterUuid,
  fieldDefs,
}) => {
  const { isOpen, openDrawer, closeDrawer } = useDrawerState(false)
  const isFieldLimitReached = fieldDefs.length >= MASTER_FIELD_DEF_LIMIT

  return (
    <>
      <Tooltip
        title={isFieldLimitReached ? `項目数が上限に達しています` : ''}
        arrow
      >
        <span>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openDrawer}
            disabled={isFieldLimitReached}
          >
            項目を追加する
          </Button>
        </span>
      </Tooltip>
      <MasterFieldDefAddDrawer
        open={isOpen}
        closeDrawer={closeDrawer}
        masterUuid={masterUuid}
        fieldDefs={fieldDefs}
      />
    </>
  )
}

type DrawerProps = {
  open: boolean
  closeDrawer: () => void
  masterUuid: string
  fieldDefs: MasterFieldDef[]
}

const MasterFieldDefAddDrawer: FC<DrawerProps> = ({
  open,
  closeDrawer,
  masterUuid,
  fieldDefs,
}) => {
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar()
  const addFieldDefMutation = useMutationAddMasterFieldDef(masterUuid)
  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      fieldDefName: '',
      fieldDefType: '',
    },
  })

  const existingFieldNames = fieldDefs.map(field => field.name)

  const validateFieldDefName = (value: string) => {
    if (!value) {
      return '項目名を入力してください'
    }
    if (value.length > 30) {
      return '項目名は30文字以内で入力してください'
    }
    if (existingFieldNames.includes(value)) {
      return 'この項目名は既に使用されています'
    }
    return true
  }

  const handleClose = useCallback(() => {
    reset()
    closeDrawer()
  }, [reset, closeDrawer])

  const handleDrawerClose = useModalClosePreventionIsPending(
    handleClose,
    addFieldDefMutation.isPending,
  )

  const onSave = useCallback(
    (data: FormData) => {
      if (data.fieldDefType === '') {
        return
      }

      addFieldDefMutation.mutate(
        {
          name: data.fieldDefName,
          fieldType: data.fieldDefType,
        },
        {
          onSuccess: () => {
            showSuccessSnackbar(`「${data.fieldDefName}」を追加しました`)
            handleClose()
          },
          onError: (error: unknown) => {
            if (isHTTPError(error)) {
              switch (error.status) {
                case 400:
                case 409:
                  showErrorSnackbar(error.errors.join('\n'))
                  break
                default:
                  showErrorSnackbar(
                    `「${data.fieldDefName}」の追加に失敗しました`,
                  )
                  break
              }
              return
            }
            showErrorSnackbar(`「${data.fieldDefName}」の追加に失敗しました`)
          },
        },
      )
    },
    [addFieldDefMutation, showSuccessSnackbar, handleClose, showErrorSnackbar],
  )

  const fieldDefNameId = useId()
  const fieldDefTypeId = useId()
  const fieldDefTypeLabelId = useId()

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 600,
        },
      }}
    >
      <Header>
        <HeaderBackButtonMui
          onClick={handleClose}
          style={{ fontWeight: 'bold', fontSize: '15px' }}
          disabled={addFieldDefMutation.isPending}
        />
        <HeaderTitle title="項目を追加する" />
      </Header>

      <Stack
        component="form"
        onSubmit={handleSubmit(onSave)}
        sx={{ height: '100%' }}
      >
        <Stack sx={{ flex: 1 }}>
          <Stack spacing={2} sx={{ p: 3 }}>
            <Stack spacing={1}>
              <Typography
                htmlFor={fieldDefNameId}
                component="label"
                sx={{
                  fontWeight: 'bold',
                  display: 'block',
                }}
              >
                項目名
              </Typography>
              <Controller
                name="fieldDefName"
                control={control}
                rules={{
                  required: '項目名を入力してください',
                  validate: validateFieldDefName,
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id={fieldDefNameId}
                    {...field}
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    variant="outlined"
                  />
                )}
              />
            </Stack>
            <Stack spacing={1}>
              <Typography
                htmlFor={fieldDefTypeId}
                component="label"
                sx={{
                  fontWeight: 'bold',
                  display: 'block',
                }}
              >
                形式
              </Typography>
              <Controller
                name="fieldDefType"
                control={control}
                rules={{ required: '形式を選択してください' }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth error={!!error}>
                    <InputLabel id={fieldDefTypeLabelId}>
                      形式を選択してください
                    </InputLabel>
                    <Select
                      {...field}
                      id={fieldDefTypeId}
                      labelId={fieldDefTypeLabelId}
                      label="形式を選択してください"
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            '& .MuiMenuItem-root': {
                              fontSize: '16px',
                              fontWeight: 'bold',
                            },
                          },
                        },
                      }}
                    >
                      {ITEM_TYPE_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Stack>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{
            p: 3,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: gray[2],
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={handleClose}
            sx={{ minWidth: 100 }}
            disabled={addFieldDefMutation.isPending}
          >
            キャンセル
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ minWidth: 100 }}
            disabled={addFieldDefMutation.isPending}
          >
            {addFieldDefMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  )
}
